import React, { useState, useEffect, useRef } from 'react';
import { Building2, Truck, Mail, Phone, Users, Package, Zap, ArrowRight, Target, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';

const FormSection = () => {
    const [userType, setUserType] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
    const [formData, setFormData] = useState({
        companyName: '',
        companyAddress: '',
        email: '',
        phone: '',
        companySize: '',
        fleetSize: '',
        purpose: '',
        purposeDetails: ''
    });

    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        const requiredFields = ['companyName', 'companyAddress', 'email', 'phone', 'purpose'];
        if (userType === 'shipper') {
            requiredFields.push('companySize');
        }

        return requiredFields.every(field => formData[field]?.trim());
    };

    const submitToAPI = async (formData, userType) => {
        const apiUrl = userType === 'shipper'
            ? 'https://api.roadexpressfreight.com/api/v1/web/supportCustomer'
            : 'https://api.roadexpressfreight.com/api/v1/web/supportTransporter';

        const payload = {
            name: formData.companyName,
            email: formData.email,
            subject: `${userType === 'shipper' ? 'Customer (Shipper)' : 'Transporter (Carrier)'} Application - ${formData.companyName}`,
            size: userType === 'shipper' ? formData.companySize : formData.fleetSize || '',
            address: formData.companyAddress,
            phone: formData.phone,
            priority: formData.purpose,
            message: formData.purposeDetails || 'No additional details provided.'
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            return { success: true, data: result };
        } catch (error) {
            console.error('API submission failed:', error);
            throw error;
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setSubmitStatus('error');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Send email
            const apiResult = await submitToAPI(formData, userType);

            if (apiResult.success) {
                setSubmitStatus('success');
                // Reset form after successful submission
                setTimeout(() => {
                    setFormData({
                        companyName: '',
                        companyAddress: '',
                        email: '',
                        phone: '',
                        companySize: '',
                        fleetSize: '',
                        purpose: '',
                        purposeDetails: ''
                    });
                    setUserType('');
                    setSubmitStatus(null);
                }, 3000);
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getPurposeOptions = () => {
        if (userType === 'shipper') {
            return [
                'Regular freight shipping needs',
                'Express delivery services',
                'Bulk cargo transportation',
                'Temperature-controlled shipping',
                'Warehousing and distribution',
                'Last-mile delivery solutions',
                'E-commerce logistics partnership',
                'Supply chain optimization',
                'Other (please specify in details below)'
            ];
        } else {
            return [
                'Join as transportation partner',
                'Expand service coverage area',
                'Access to more shipping contracts',
                'Fleet utilization optimization',
                'Technology integration support',
                'Regular cargo volume guarantee',
                'Seasonal transportation needs',
                'Specialized cargo handling',
                'Other (please specify in details below)'
            ];
        }
    };

    return (
        <section
            ref={sectionRef}
            className="w-full bg-gray-100 relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">

                {/* Header Section */}
                <div className="mb-16">
                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                    >
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-px bg-[#F05A28] mr-4"></div>
                            <span className="text-[#F05A28] font-semibold tracking-wider text-sm uppercase flex items-center">
                                <Zap className="w-4 h-4 mr-2" />
                                Join Our Network
                            </span>
                        </div>

                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                            Partner with
                            <br />
                            <span className="text-[#F05A28] italic">India's leading logistics platform</span>
                        </h1>

                        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
                            Connect with trusted partners in our comprehensive logistics ecosystem.
                            Whether you're shipping goods or providing transport services, we're here to streamline your operations.
                        </p>
                    </div>
                </div>

                {/* Success/Error Message */}
                {submitStatus && (
                    <div className={`mb-8 p-6 rounded-xl flex items-center space-x-3 ${submitStatus === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                        }`}>
                        {submitStatus === 'success' ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                            <AlertCircle className="w-6 h-6 text-red-600" />
                        )}
                        <div>
                            <p className="font-semibold">
                                {submitStatus === 'success'
                                    ? 'Application Submitted Successfully!'
                                    : 'Submission Failed'
                                }
                            </p>
                            <p className="text-sm">
                                {submitStatus === 'success'
                                    ? 'Your application has been sent to our team. We will contact you within 24-48 hours.'
                                    : 'Please check all required fields and try again.'
                                }
                            </p>
                        </div>
                    </div>
                )}

                {/* User Type Selection */}
                <div
                    className={`bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-12 mb-8 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                    style={{ transitionDelay: '0.2s' }}
                >
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 flex items-center">
                        <Users className="w-8 h-8 mr-4 text-[#F05A28]" />
                        Select Your Role
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div
                            onClick={() => setUserType('shipper')}
                            className={`group relative cursor-pointer rounded-2xl border-2 p-8 transition-all duration-500 ${userType === 'shipper'
                                ? 'border-[#F05A28] bg-orange-50'
                                : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                                }`}
                        >
                            <div className="flex items-start">
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 ${userType === 'shipper' ? 'bg-[#F05A28]' : 'bg-gray-100 group-hover:bg-gray-200'
                                    }`}>
                                    <Package className={`w-8 h-8 ${userType === 'shipper' ? 'text-white' : 'text-gray-600'
                                        }`} />
                                </div>
                                <div className="ml-6">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Customer (Shipper)</h3>
                                    <p className="text-gray-600 leading-relaxed">
                                        I need to ship goods and require reliable logistics services for my business operations.
                                    </p>
                                </div>
                            </div>

                            {/* Accent line */}
                            <div className={`absolute bottom-0 left-8 right-8 h-1 bg-[#F05A28] rounded-full transition-all duration-500 ${userType === 'shipper' ? 'scale-x-100' : 'scale-x-0'
                                }`}></div>
                        </div>

                        <div
                            onClick={() => setUserType('carrier')}
                            className={`group relative cursor-pointer rounded-2xl border-2 p-8 transition-all duration-500 ${userType === 'carrier'
                                ? 'border-[#F05A28] bg-orange-50'
                                : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                                }`}
                        >
                            <div className="flex items-start">
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 ${userType === 'carrier' ? 'bg-[#F05A28]' : 'bg-gray-100 group-hover:bg-gray-200'
                                    }`}>
                                    <Truck className={`w-8 h-8 ${userType === 'carrier' ? 'text-white' : 'text-gray-600'
                                        }`} />
                                </div>
                                <div className="ml-6">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Transporter (Carrier)</h3>
                                    <p className="text-gray-600 leading-relaxed">
                                        I provide transportation and logistics services to businesses across various sectors.
                                    </p>
                                </div>
                            </div>

                            {/* Accent line */}
                            <div className={`absolute bottom-0 left-8 right-8 h-1 bg-[#F05A28] rounded-full transition-all duration-500 ${userType === 'carrier' ? 'scale-x-100' : 'scale-x-0'
                                }`}></div>
                        </div>
                    </div>
                </div>

                {/* Main Form */}
                {userType && (
                    <div
                        className={`bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-12 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                        style={{ transitionDelay: '0.4s' }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 flex items-center">
                            <Building2 className="w-8 h-8 mr-4 text-[#F05A28]" />
                            Company Information
                        </h2>

                        <div className="space-y-12">
                            {/* Company Details */}
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                                    <div className="w-2 h-2 bg-[#F05A28] rounded-full mr-3"></div>
                                    Company Details
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-1">
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Company Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleInputChange}
                                            placeholder="Enter your company name"
                                            className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg"
                                            required
                                        />
                                    </div>
                                    {userType === 'shipper' && (
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                                Company Size *
                                            </label>
                                            <select
                                                name="companySize"
                                                value={formData.companySize}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg"
                                                required
                                            >
                                                <option value="">Select company size</option>
                                                <option value="1-10">1-10 employees</option>
                                                <option value="11-50">11-50 employees</option>
                                                <option value="51-200">51-200 employees</option>
                                                <option value="201-1000">201-1000 employees</option>
                                                <option value="1000+">1000+ employees</option>
                                            </select>
                                        </div>
                                    )}
                                    {userType === 'carrier' && (
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                                Fleet Size
                                            </label>
                                            <select
                                                name="fleetSize"
                                                value={formData.fleetSize}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg"
                                            >
                                                <option value="">Select fleet size</option>
                                                <option value="1-5">1-5 vehicles</option>
                                                <option value="6-20">6-20 vehicles</option>
                                                <option value="21-50">21-50 vehicles</option>
                                                <option value="51-100">51-100 vehicles</option>
                                                <option value="100+">100+ vehicles</option>
                                            </select>
                                        </div>
                                    )}

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Company Address *
                                        </label>
                                        <input
                                            type="text"
                                            name="companyAddress"
                                            value={formData.companyAddress}
                                            onChange={handleInputChange}
                                            placeholder="Street address, building, suite"
                                            className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="border-t border-gray-100 pt-12">
                                <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                                    <div className="w-2 h-2 bg-[#F05A28] rounded-full mr-3"></div>
                                    Contact Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Email Address *
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="email@company.com"
                                                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Phone Number *
                                        </label>
                                        <div className="relative">
                                            <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder="+91 9876543210"
                                                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Purpose Section */}
                            <div className="border-t border-gray-100 pt-12">
                                <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                                    <div className="w-2 h-2 bg-[#F05A28] rounded-full mr-3"></div>
                                    Purpose & Requirements
                                </h3>
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Primary Purpose *
                                        </label>
                                        <div className="relative">
                                            <Target className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                                            <select
                                                name="purpose"
                                                value={formData.purpose}
                                                onChange={handleInputChange}
                                                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg"
                                                required
                                            >
                                                <option value="">Select your primary purpose</option>
                                                {getPurposeOptions().map((option, index) => (
                                                    <option key={index} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Additional Details & Requirements
                                        </label>
                                        <div className="relative">
                                            <MessageSquare className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                                            <textarea
                                                name="purposeDetails"
                                                value={formData.purposeDetails}
                                                onChange={handleInputChange}
                                                placeholder="Please provide more details about your specific requirements, expected volumes, preferred routes, special handling needs, or any other relevant information..."
                                                rows={5}
                                                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F05A28] focus:border-transparent transition-all text-lg resize-vertical"
                                            />
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">
                                            Help us understand your needs better by providing specific details about your requirements.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="border-t border-gray-100 pt-12">
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || !validateForm()}
                                    className={`group px-12 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-3 text-lg shadow-lg hover:shadow-xl ${isSubmitting || !validateForm()
                                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                        : 'bg-[#F05A28] text-white hover:bg-orange-600'
                                        }`}
                                >
                                    <span>
                                        {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                                    </span>
                                    {!isSubmitting && (
                                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                    )}
                                </button>
                                <p className="text-gray-600 mt-6 leading-relaxed">
                                    By submitting this application, you agree to our terms of service and privacy policy.
                                    Our team will review your application and get back to you within 24-48 hours with next steps.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default FormSection;